import { lastYear } from '~/helpers/date';
import type { CountryKey, FamilyMember, Marital, PastTravels, CountryIso, Family, CountryApplicantMap, ApplicantFiles, Religion, Branch } from '~/types';
import passportFactory from './passport-factory';
import generateUUID from '~/helpers/uuid';
import { addressFactory, extraNationalityFactory, occupationFactory, pastVisitFactory } from './mini-factories';
import { APPLICANT_PHOTO_TYPES } from '~/assets/constants';

interface Config<C extends CountryKey> {
  country: C;
  branch: Branch;
}

const FACTORIES: { [C in CountryKey]: (config: Config<C>) => CountryApplicantMap[C] } = {
  india: indiaApplicantFactory,
  canada: canadaApplicantFactory,
  sri_lanka: sriLankaApplicantFactory,
  morocco: moroccoApplicantFactory,
  new_zealand: newZealandApplicantFactory,
  vietnam: vietnamApplicantFactory,
  usa: usaApplicantFactory,
  korea: koreaApplicantFactory,
  cambodia: cambodiaApplicantFactory,
  israel: israelApplicantFactory,
  saudi_arabia: saudiArabiaApplicantFactory,
  bahrain: bahrainApplicantFactory,
  indonesia: indonesiaApplicantFactory,
  uk: ukApplicantFactory,
  togo: togoApplicantFactory,
  thailand: thailandApplicantFactory,
  schengen: schengenApplicantFactory
};

export default function applicantFactory<C extends CountryKey>(config: Config<C>): CountryApplicantMap[C] {
  return FACTORIES[config.country](config);
}

function schengenApplicantFactory<C extends 'schengen'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue'] }),
    address: addressFactory(baseCountry),
    occupation: occupationFactory(baseCountry),
    cityOfBirth: '',
    maritalStatus: 'single'
  };
}

function thailandApplicantFactory<C extends 'thailand'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
    extraNationality: extraNationalityFactory(),
    cityOfBirth: '',
    address: addressFactory(baseCountry),
    occupation: occupationFactory(baseCountry),
    maritalStatus: 'single',
    visited: false,
    files: filesFactory()
  };
}

function togoApplicantFactory<C extends 'togo'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
    address: addressFactory(baseCountry),
    family: familyFactory(baseCountry),
    occupation: 'employee',
    files: filesFactory()
  };
}

function ukApplicantFactory<C extends 'uk'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  const passport = passportFactory(baseCountry);
  return {
    id: generateUUID('applicant'),
    passport: {
      firstName: passport.firstName,
      lastName: passport.lastName,
    },
    address: addressFactory(baseCountry),
    extraNationality: extraNationalityFactory(),
    occupation: occupationFactory(baseCountry),
    crime: '',
    files: filesFactory()
  };
}

function indonesiaApplicantFactory<C extends 'indonesia'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
    files: filesFactory()
  };
}

function bahrainApplicantFactory<C extends 'bahrain'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  const passport = passportFactory(baseCountry);
  return {
    id: generateUUID('applicant'),
    passport: {
      number: passport.number,
      nationality: passport.nationality,
      firstName: passport.firstName,
      lastName: passport.lastName,
      dateOfBirth: passport.dateOfBirth,
      countryOfBirth: passport.countryOfBirth,
      sex: passport.sex,
      dateOfIssue: passport.dateOfIssue,
      dateOfExpiry: passport.dateOfExpiry
    },
    occupation: occupationFactory(baseCountry),
    files: filesFactory()
  };
}

function saudiArabiaApplicantFactory<C extends 'saudi_arabia'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
    address: addressFactory(baseCountry),
    religion: religionFactory(baseCountry),
    occupation: 'employee',
    maritalStatus: 'single',
    guardianPassport: '',
    files: filesFactory()
  };
}

function israelApplicantFactory<C extends 'israel'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue'] }),
    extraNationality: extraNationalityFactory(),
    address: addressFactory(baseCountry),
    family: familyFactory(baseCountry),
    occupation: occupationFactory(baseCountry),
    pastVisit: { visited: false, year: '' },
    files: filesFactory()
  };
}

function cambodiaApplicantFactory<C extends 'cambodia'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue'] }),
    occupationStatus: 'employee',
    files: filesFactory()
  };
}

function koreaApplicantFactory<C extends 'korea'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    address: addressFactory(baseCountry),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue', 'dateOfIssue'] }),
    extraNationality: extraNationalityFactory(),
    cityOfBirth: '',
    crime: '',
    occupation: occupationFactory(baseCountry),
    visited: false,
    lastTravel: { traveled: false, country: 'IND', from: '', until: '' },
    files: filesFactory()
  };
}

function usaApplicantFactory<C extends 'usa'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  const passport = passportFactory(baseCountry);
  return {
    id: generateUUID('applicant'),
    passport: {
      firstName: passport.firstName,
      lastName: passport.lastName,
      countryOfBirth: passport.countryOfBirth,
      dateOfIssue: passport.dateOfIssue,
      dateOfExpiry: passport.dateOfExpiry,
      dateOfBirth: passport.dateOfBirth,
      sex: passport.sex,
      nationality: passport.nationality,
      number: passport.number
    },
    extraNationality: extraNationalityFactory(),
    cityOfBirth: '',
    address: addressFactory(baseCountry),
    family: familyFactory(baseCountry),
    crime: '',
    pastRejection: '',
    occupation: occupationFactory(baseCountry),
    files: filesFactory()
  };
}

function vietnamApplicantFactory<C extends 'vietnam'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    idNumber: '',
    occupation: occupationFactory(baseCountry),
    extraNationality: extraNationalityFactory(),
    address: addressFactory(baseCountry),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue'] }),
    religion: religionFactory(baseCountry),
    files: filesFactory()
  };
}

function newZealandApplicantFactory<C extends 'new_zealand'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue', 'dateOfIssue'] }),
    idNumber: '',
    cityOfBirth: '',
    files: filesFactory()
  };
}

function moroccoApplicantFactory<C extends 'morocco'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry, { exclusions: ['placeOfIssue'] }),
    occupation: 'employee',
    files: filesFactory()
  };
}

function sriLankaApplicantFactory<C extends 'sri_lanka'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
  };
}

function canadaApplicantFactory<C extends 'canada'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
    address: addressFactory(baseCountry),
    occupation: occupationFactory(baseCountry),
    marital: maritalFactory(baseCountry),
    crime: '',
    pastTravels: pastTravelsFactory()
  };
}

function indiaApplicantFactory<C extends 'india'>(config: Config<C>): CountryApplicantMap[C] {
  const { baseCountry } = config.branch;
  return {
    id: generateUUID('applicant'),
    passport: passportFactory(baseCountry),
    extraNationality: extraNationalityFactory(),
    idNumber: '',
    religion: religionFactory(baseCountry),
    address: addressFactory(baseCountry),
    crime: '',
    family: familyFactory(baseCountry),
    occupation: occupationFactory(baseCountry),
    military: {
      served: false,
      role: 'fighter',
      rank: 'staff_sergeant',
    },
    pastTravels: pastTravelsFactory(),
    files: filesFactory()
  };
}

function pastTravelsFactory(): PastTravels {
  return {
    pastVisit: pastVisitFactory(),
    rejection: '',
    SAARC: {
      visited: false,
      countries: [],
      year: lastYear()
    }
  };
}

function maritalFactory(baseCountry: CountryIso): Marital {
  return {
    status: 'single',
    spouse: familyMemberFactory(baseCountry)
  };
}

function familyMemberFactory(baseCountry: CountryIso): FamilyMember {
  return {
    name: '',
    countryOfBirth: baseCountry
  };
}

function filesFactory(): ApplicantFiles {
  const images: ApplicantFiles = APPLICANT_PHOTO_TYPES.reduce((acc, type) => {
    acc[type] = '';
    return acc;
  }, {} as ApplicantFiles);
  return images;
}

function familyFactory(baseCountry: CountryIso): Family {
  return {
    father: familyMemberFactory(baseCountry),
    mother: familyMemberFactory(baseCountry),
    marital: maritalFactory(baseCountry)
  };
}

function religionFactory(baseCountry: CountryIso): Religion {
  switch (baseCountry) {
    case 'ISR': return 'judaism';
    default: return 'christianity';
  }
}